// Loading the rules data.
//
// Two entry points because the engine runs in two places: the browser fetches
// the generated JSON over HTTP, and tests and tooling read it off disk. Both
// produce the same `Rules` object, so nothing downstream knows the difference.

import type { CardData } from '../cards/types.ts';
import { CardIndex } from './cards.ts';
import type { ScriptIndex } from './effects.ts';
import type { Rules } from './state.ts';

export interface LoadedRules extends Rules {
  /** Ids that have artwork on disk; decks are built only from these. */
  available: Set<string>;
}

export function makeRules(
  cards: CardData[], scripts: ScriptIndex, available: Iterable<string>,
): LoadedRules {
  return { index: new CardIndex(cards), scripts, available: new Set(available) };
}

/** Browser path: fetches from the generated asset directory. */
export async function loadRules(base = ''): Promise<LoadedRules> {
  const get = async <T>(path: string): Promise<T> => {
    const res = await fetch(`${base}${path}`);
    if (!res.ok) {
      throw new Error(
        `Missing ${path} (${res.status}). Run "npm run data:all" to build the asset pipeline.`,
      );
    }
    return res.json() as Promise<T>;
  };

  const [cards, scripts, manifest] = await Promise.all([
    get<CardData[]>('/generated/cards.json'),
    get<ScriptIndex>('/generated/effects.json'),
    get<string[]>('/generated/art-manifest.json'),
  ]);

  return makeRules(cards, scripts, manifest);
}
